import { useState } from 'react';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectAllEmployees } from '../../slices/employees/employeesSlice';
import {
  selectAllSuppliers,
  selectSelectedSupplier,
  setSelectedSupplier,
} from '../../slices/suppliers/suppliersSlice';
import { selectAllCustomers } from '../../slices/customers/customersSlice';
import { selectBusinessProfileContacts } from '../../slices/business-profile/businessProfileSlice';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { AIDocumentContractModel } from '../../models/ai.document.contract.model';
import { Document } from '../../pages/document-vault/components/document-list-vault/types';
import { NWClient } from '../../client/NWClient';
import { selectUser } from '../../slices/user/userSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';

export const useAssignProfilePersonModal = (
  category?: string,
  document?: DocumentVaultItemModel | AIDocumentContractModel
) => {
  const [assignee, setAssignee] = useState<string>('');
  const employees = useAppSelector(selectAllEmployees);
  const suppliers = useAppSelector(selectAllSuppliers);
  const customers = useAppSelector(selectAllCustomers);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const currentUser = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const selectedSupplier = useAppSelector(selectSelectedSupplier);

  const handleSetSelectedSupplier = (supplierId: string) => {
    dispatch(setSelectedSupplier(supplierId));
  };

  const employeesOptions = employees?.map((employee) => ({
    value: employee.id.toString(),
    label: `${employee.full_name}`,
  }));

  const suppliersOptions = suppliers.map((supplier) => ({
    value: supplier.id.toString(),
    label: `${supplier.name}`,
  }));

  const customersOptions = customers?.map((customer) => ({
    value: customer.id.toString(),
    label: `${customer.customer_name}`,
  }));

  const businessContactsOptions = businessContacts?.map((contact) => ({
    value: contact.id.toString(),
    label: `${contact.contact_name}`,
  }));

  const getProfileNames = () => {
    switch (category) {
      case 'employment':
        return employeesOptions;
      case 'customers':
        return customersOptions;
      case 'suppliers':
        return suppliersOptions;
      case 'business':
        return businessContactsOptions;
      default:
        return [];
    }
  };
  const getProfileLabelsProp = () => {
    if (category === 'employment') {
      return 'employees';
    } else if (category === 'business') {
      return 'business_profile_contacts';
    } else {
      return category ?? '';
    }
  };

  const patchDocumentWithProfilePerson = (): Promise<Document> => {
    const data = {
      [getProfileLabelsProp()]: [Number(assignee)],
      user: currentUser.id,
    };
    return NWClient.patch<Document>(
      `${document && 'contract_generation' in document ? 'document-contract' : 'document-upload'}/${document?.id}/`,
      null,
      data
    );
  };

  const getSupplierOptions = () => {
    return suppliersOptions || [];
  };

  const getSupplierContactOptions = (supplierId?: string) => {
    if (!supplierId) return [];

    const supplier = suppliers?.find((s) => s.id.toString() === supplierId);
    return (
      supplier?.contacts?.map((contact) => ({
        value: contact.id?.toString() ?? '',
        label: `${contact.full_name}`,
      })) || []
    );
  };

  const checkProfilePersonsAvailability = (category: string) => {
    switch (category) {
      case 'employment':
        return employees?.length > 0;
      case 'business':
        return (businessContacts?.length ?? 0) > 0;
      case 'customers':
        return customers?.length > 0;
      case 'suppliers':
        return suppliers?.length > 0;
    }
  };

  return {
    assignee,
    setAssignee,
    selectedSupplier,
    setSelectedSupplier: handleSetSelectedSupplier,
    getProfileNames,
    getSupplierOptions,
    getSupplierContactOptions,
    patchDocumentWithProfilePerson,
    checkProfilePersonsAvailability,
  };
};
